import { Grid, SxProps, Theme } from "@mui/material";
import { JSX, useCallback, useEffect, useMemo, useState } from "react";
import { ConditionDescriptor, ConditionField, DimensionDescriptor, ItemDataType } from "../../../../api/biClient.types";
import { ShowFieldOptionsSettings } from "../Types.ts";
import ConditionFieldOptionPopup from "./ConditionFieldOptionPopup";
import ConditionItemContainer from "./ConditionItemContainer";

interface Props {
  conditions: ConditionField[];
  detailed: boolean;
  canBeRemoved: boolean;
  readonly?: boolean;
  disableEqualitySelection?: boolean;
  dimensions: DimensionDescriptor[];
  sx?: SxProps<Theme>;
  renderCustomId?: (field: ConditionField) => JSX.Element;
  onRemoveFields?: (field: ConditionField) => void;
  onFieldChanged: (field: ConditionField, changes: Partial<ConditionField>) => void;
}

export const Conditions = ({
  conditions,
  detailed,
  canBeRemoved,
  readonly,
  onRemoveFields,
  onFieldChanged,
  disableEqualitySelection,
  renderCustomId,
  dimensions,
  sx,
}: Props) => {
  const [optionsAnchorEl, setOptionsAnchorEl] = useState<HTMLElement | null>(null);
  const [optionField, setOptionField] = useState<ConditionField>();
  const [showOnlySelected, setShowOnlySelected] = useState(false);

  const dictionary = useMemo(() => {
    return (
      dimensions.find((d) => d.name === optionField?.meta.name)?.dictionary || (optionField?.meta.dictionary ?? [])
    );
  }, [dimensions, optionField]);

  useEffect(() => {
    if (optionField !== undefined) {
      const field = conditions.find((f) => f.config.guid === optionField.config.guid);
      if (field !== undefined) {
        setOptionField(field);
      }
    }
  }, [conditions, optionField]);

  const handleClosePopup = useCallback(() => {
    setOptionsAnchorEl(null);
  }, []);

  const onShowOptions = useCallback((settings: ShowFieldOptionsSettings<ConditionField>) => {
    setOptionField(settings.field);
    setOptionsAnchorEl(settings.ref);
    setShowOnlySelected(settings.showOnlySelected);
  }, []);

  const onApplyFilter = useCallback(
    (filter: ConditionDescriptor) => {
      if (optionField) {
        onFieldChanged(optionField, { config: { ...optionField.config, filter } });
      }
      handleClosePopup();
    },
    [optionField, onFieldChanged, handleClosePopup]
  );

  return (
    <Grid container sx={{ gap: 1, width: "100%", ...sx }}>
      {conditions.map((f) => {
        return (
          <ConditionItemContainer
            key={f.config.guid}
            field={f}
            detailed={detailed}
            canBeRemoved={canBeRemoved}
            readonly={readonly}
            onRemoveItem={onRemoveFields}
            showOptions={onShowOptions}
            disableEqualitySelection={disableEqualitySelection}
          />
        );
      })}
      {optionsAnchorEl && optionField && (
        <ConditionFieldOptionPopup
          anchorEl={optionsAnchorEl}
          field={optionField}
          showOnlySelectedByDefault={showOnlySelected}
          canBeRemoved={canBeRemoved}
          readonly={readonly}
          save={onApplyFilter}
          resizable={optionField.meta.type !== ItemDataType.Date}
          readonlyCustomLabel={true}
          dictionary={dictionary}
          onRemoveItem={() => {
            if (!onRemoveFields) return;
            onRemoveFields(optionField);
            handleClosePopup();
          }}
          disableEqualitySelection={disableEqualitySelection}
          close={handleClosePopup}
          CustomId={renderCustomId?.(optionField)}
        />
      )}
    </Grid>
  );
};

export default Conditions;
